import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, SlidersHorizontal, History, AlertTriangle, Search, FileSpreadsheet } from "lucide-react";
import { AdjustInventoryModal, InventoryHistoryModal, InventoryDetailModal, StatusPill, fmtDate } from "./InventoryShared";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["AVAILABLE", "LOW_STOCK", "OUT_OF_STOCK", "HELD", "RESERVED", "PARTIALLY_RESERVED", "DAMAGED", "INACTIVE"];
const FLAGS = [["", "All stock"], ["low_stock", "Low stock"], ["out_of_stock", "Out of stock"], ["held", "Held"], ["reserved", "Reserved"], ["damaged", "Damaged"]];
const SORTS = [["updated", "Last updated"], ["product", "Product"], ["sku", "SKU"], ["quantity", "Quantity"], ["available", "Available"], ["status", "Status"], ["location", "Location"]];

export const InventoryTab = ({ initialFlag = "" }) => {
  const { user } = useAuth();
  const canSeeCost = user?.permissions?.includes("VIEW_COST");
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", category_id: "", location_id: "", status: "", condition: "", inventory_type: "", flag: initialFlag, sort: "updated", order: "desc", page: 1 });
  const [modal, setModal] = useState(null);
  const [demo, setDemo] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries({ ...filters, per_page: 50 }).filter(([, v]) => v !== ""));
    api.get("/inventory", { params }).then((r) => setData(r.data)).catch(() => toast.error("Could not load inventory")).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, filters.q ? 250 : 0); return () => clearTimeout(t); }, [load, filters.q]);
  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data));
    api.get("/inventory/locations").then((r) => setLocs(r.data));
    api.get("/inventory/demo/count").then((r) => setDemo(r.data));
  }, []);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));
  const closeAndReload = () => { setModal(null); load(); api.get("/inventory/demo/count").then((r) => setDemo(r.data)); };

  const removeDemo = async () => {
    const typed = window.prompt(`This permanently deletes ${demo?.products || 0} DEMO products and their inventory. Real inventory is never touched.\n\nType REMOVE DEMO DATA to confirm:`);
    if (typed === null) return;
    try {
      const r = await api.post("/inventory/demo/remove", { confirm: typed });
      toast.success(`Removed ${r.data.products_removed} demo products`); closeAndReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportExcel = async () => {
    try {
      const params = Object.fromEntries(Object.entries({
        q: filters.q, location_id: filters.location_id, category_id: filters.category_id, status: filters.status,
        condition: filters.condition, inventory_type: filters.inventory_type, flag: filters.flag,
      }).filter(([, v]) => v !== ""));
      const r = await api.get("/inventory/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "dealswagon-stock-in-hand.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Stock Excel downloaded");
    } catch { toast.error("Could not export stock"); }
  };

  const sel = "h-10 rounded-lg border border-slate-200 px-2 bg-white text-sm";
  return (
    <div data-testid="inventory-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div>
          <div className="font-heading text-2xl text-brand-navy">Inventory</div>
          <div className="text-xs text-slate-500">{data.total} records · single source of truth for online, Prepare Order, holds &amp; reservations</div>
        </div>
        {demo?.products > 0 && (
          <button onClick={removeDemo} data-testid="remove-demo-btn" className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider border border-brand-red text-brand-red rounded-full h-9 px-4 hover:bg-red-50">
            <AlertTriangle className="h-3.5 w-3.5" /> Remove demo data ({demo.products})
          </button>
        )}
        <button onClick={exportExcel} data-testid="inventory-export-excel-btn" className="inline-flex items-center gap-1 bg-brand-navy text-white rounded-full h-9 px-4 font-bold text-sm">
          <FileSpreadsheet className="h-4 w-4" /> Download Excel
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-4 grid gap-2 md:grid-cols-4 lg:grid-cols-8">
        <label className="relative md:col-span-2">
          <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
          <input data-testid="inventory-search" placeholder="Search name, SKU, barcode, brand" value={filters.q} onChange={(e) => set("q", e.target.value)}
            className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm" />
        </label>
        <select data-testid="filter-location" className={sel} value={filters.location_id} onChange={(e) => set("location_id", e.target.value)}>
          <option value="">All locations</option>{locs.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
        </select>
        <select data-testid="filter-category" className={sel} value={filters.category_id} onChange={(e) => set("category_id", e.target.value)}>
          <option value="">All categories</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="filter-status" className={sel} value={filters.status} onChange={(e) => set("status", e.target.value)}>
          <option value="">Any status</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="filter-condition" className={sel} value={filters.condition} onChange={(e) => set("condition", e.target.value)}>
          <option value="">Any condition</option>{["NEW", "OPEN BOX", "LIKE NEW", "USED", "RETURNED", "REFURBISHED", "CLEARANCE", "CUSTOMER RETURN"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="filter-type" className={sel} value={filters.inventory_type} onChange={(e) => set("inventory_type", e.target.value)}>
          <option value="">Any type</option><option>STANDARD</option><option>UNIQUE_ITEM</option>
        </select>
        <select data-testid="filter-flag" className={sel} value={filters.flag} onChange={(e) => set("flag", e.target.value)}>
          {FLAGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="flex gap-1 lg:col-span-8">
          <span className="text-xs text-slate-500 self-center mr-1">Sort</span>
          <select data-testid="sort-field" className={sel} value={filters.sort} onChange={(e) => set("sort", e.target.value)}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="sort-order" onClick={() => set("order", filters.order === "desc" ? "asc" : "desc")} className="h-10 px-3 rounded-lg border border-slate-200 text-sm font-bold">{filters.order === "desc" ? "↓ Desc" : "↑ Asc"}</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Image", "Product", "SKU", "Barcode", "Category", "Location", "On hand", "Available", "Held", "Reserved", "Sold", "Status", "Last updated", "Actions"].map((h) => <th key={h} className="p-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={14} className="p-6 text-center text-slate-400">Loading...</td></tr> :
                data.items.length === 0 ? <tr><td colSpan={14} className="p-6 text-center text-slate-400">No inventory matches these filters.</td></tr> :
                  data.items.map((r) => (
                    <tr key={r.inventory.inventory_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`inventory-row-${r.product.sku}`}>
                      <td className="p-3">{r.product.featured_image ? <img src={r.product.featured_image} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-slate-100" />}</td>
                      <td className="p-3 font-bold max-w-[220px]"><div className="truncate">{r.product.product_name}</div>
                        <div className="text-[10px] text-slate-400 font-normal">{r.inventory.inventory_type === "UNIQUE_ITEM" ? "UNIQUE ITEM" : "STANDARD"}{r.inventory.is_demo && " · DEMO"}</div></td>
                      <td className="p-3 text-slate-600 whitespace-nowrap">{r.product.sku || <span className="text-brand-red font-bold inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />No SKU</span>}</td>
                      <td className="p-3 text-slate-500">{r.inventory.barcode || "—"}</td>
                      <td className="p-3">{r.category_name || "—"}</td>
                      <td className="p-3 whitespace-nowrap">{r.location_name}</td>
                      <td className="p-3 font-bold">{r.inventory.quantity_on_hand}</td>
                      <td className="p-3 font-bold text-brand-navy" data-testid={`available-${r.product.sku}`}>{r.inventory.quantity_available}</td>
                      <td className="p-3">{r.inventory.quantity_held}</td>
                      <td className="p-3">{r.inventory.quantity_reserved}</td>
                      <td className="p-3">{r.inventory.quantity_sold}</td>
                      <td className="p-3"><StatusPill status={r.inventory.inventory_status} /></td>
                      <td className="p-3 text-slate-500 whitespace-nowrap">{fmtDate(r.inventory.updated_at)}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button title="View" data-testid={`inv-view-${r.product.sku}`} onClick={() => setModal({ type: "view", row: r })} className="p-1.5 rounded hover:bg-slate-100"><Eye className="h-4 w-4" /></button>
                          <button title="Adjust" data-testid={`inv-adjust-${r.product.sku}`} onClick={() => setModal({ type: "adjust", row: r })} className="p-1.5 rounded hover:bg-slate-100 text-brand-red"><SlidersHorizontal className="h-4 w-4" /></button>
                          <button title="History" data-testid={`inv-history-${r.product.sku}`} onClick={() => setModal({ type: "history", row: r })} className="p-1.5 rounded hover:bg-slate-100"><History className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        {data.total > 50 && (
          <div className="flex justify-between items-center p-3 border-t text-sm">
            <button disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))} className="font-bold disabled:opacity-40">← Prev</button>
            <span className="text-slate-500">Page {filters.page} of {Math.ceil(data.total / 50)}</span>
            <button disabled={filters.page * 50 >= data.total} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))} className="font-bold disabled:opacity-40">Next →</button>
          </div>
        )}
      </div>

      {modal?.type === "adjust" && <AdjustInventoryModal row={modal.row} onClose={() => setModal(null)} onDone={closeAndReload} />}
      {modal?.type === "history" && <InventoryHistoryModal row={modal.row} onClose={() => setModal(null)} />}
      {modal?.type === "view" && <InventoryDetailModal row={modal.row} canSeeCost={canSeeCost} onClose={() => setModal(null)} onChanged={closeAndReload} />}
    </div>
  );
};
